import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import Reveal from "./Reveal.jsx";
import { newsArticles } from "../data/publishedArticleIndex.generated.js";
import {
  organizations,
  portfolioApps,
  testimonials,
} from "../data/legacyContent.js";

export function OrganizationReferences() {
  return (
    <section data-analytics-section="organizations" className="kw-organizations" aria-labelledby="organizations-title">
      <div>
        <span className="kw-small-label">SELECTED ORGANIZATIONS</span>
        <h2 id="organizations-title">Experience across businesses.</h2>
        <p>Organizations featured in our published portfolio.</p>
      </div>
      <div className="kw-organization-logos">
        {organizations.map((item) => (
          <img
            key={item.name}
            src={item.image}
            alt={item.name}
            loading="lazy"
          />
        ))}
      </div>
    </section>
  );
}
export function ProductSpotlights() {
  return (
    <section data-analytics-section="product-spotlights" className="kw-section kw-product-spotlights"
      aria-labelledby="spotlights-title"
    >
      <Reveal className="kw-restored-heading">
        <div>
          <span className="kw-small-label">PRODUCT SPOTLIGHTS</span>
          <h2 id="spotlights-title">
            Ideas, built into
            <br />
            working applications.
          </h2>
        </div>
        <Link
          to="/apps"
          className="kw-text-link"
          data-analytics="portfolio_all"
        >
          Explore our app portfolio <ArrowUpRight size={17} />
        </Link>
      </Reveal>
      <div className="kw-spotlight-grid">
        {portfolioApps.slice(0, 2).map((app) => (
          <Reveal key={app.id}>
            <Link
              to={`/apps#${app.id}`}
              className={`kw-product-image kw-product-image--${app.id}`}
              aria-label={`Explore ${app.name}`}
              data-analytics={`portfolio_${app.id}`}
            >
              <img src={app.image} alt={app.alt} loading="lazy" />
            </Link>
            <div className="kw-product-description">
              <span className="kw-small-label">{app.category}</span>
              <h3>
                <Link to={`/apps#${app.id}`}>
                  {app.logo || app.id === "fitcheck" ? (
                    <img
                      className="kw-portfolio-wordmark"
                      src={app.logo || "/images/portfolio/fitcheck-logo.png"}
                      alt={app.name}
                      loading="lazy"
                    />
                  ) : (
                    <>
                      <img
                        className="kw-portfolio-icon"
                        src="/images/portfolio/stovewatch-icon.png"
                        alt=""
                        loading="lazy"
                      />
                      {app.name}
                    </>
                  )}{" "}
                  <ArrowUpRight size={21} />
                </Link>
              </h3>
              <p>{app.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
export function ClientTestimonials() {
  const [selected, setSelected] = useState(0);
  const review = testimonials[selected];
  const display = useRef(null);
  const previousHeight = useRef(0);
  useLayoutEffect(() => {
    const element = display.current;
    const height = element.scrollHeight;
    const previous = previousHeight.current;
    previousHeight.current = height;
    if (
      !previous ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const animation = element.animate(
      [{ height: `${previous}px` }, { height: `${height}px` }],
      { duration: 300, easing: "ease-out" },
    );
    return () => animation.cancel();
  }, [selected]);
  return (
    <section data-analytics-section="client-reviews" className="kw-section kw-client-reviews"
      aria-labelledby="reviews-title"
    >
      <Reveal>
        <Quote size={31} strokeWidth={1.25} className="kw-review-mark" />
        <span className="kw-small-label">CLIENT TESTIMONIALS</span>
        <h2 id="reviews-title">
          In our clients’
          <br />
          own words.
        </h2>
        <p>Feedback originally shared on the Karalewich website.</p>
        <div className="kw-review-controls">
          <button
            type="button"
            onClick={() =>
              setSelected(
                (selected + testimonials.length - 1) % testimonials.length,
              )
            }
            aria-label="Previous testimonial"
          >
            <ArrowLeft size={19} />
          </button>
          <span>
            {String(selected + 1).padStart(2, "0")} /{" "}
            {String(testimonials.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={() => setSelected((selected + 1) % testimonials.length)}
            aria-label="Next testimonial"
          >
            <ArrowRight size={19} />
          </button>
        </div>
      </Reveal>
      <div
        ref={display}
        className="kw-review-display"
        aria-live="polite"
        aria-atomic="true"
      >
        <figure key={review.name}>
          <blockquote>{review.quote}</blockquote>
          <figcaption>
            {review.image && (
              <img
                src={review.image}
                alt=""
                loading="lazy"
                width="54"
                height="54"
              />
            )}
            <div>
              <strong>{review.name}</strong>
              <span>Client testimonial</span>
            </div>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
export function NewsCards({ articles = newsArticles }) {
  return (
    <div className="kw-news-grid">
      {articles.map((article) => (
        <article key={article.slug}>
          <Link
            to={`/news/${article.slug}`}
            className="kw-news-photo"
            aria-label={`Read ${article.title}`}
            data-analytics={`news_${article.slug}`}
          >
            <img src={article.image} alt="" loading="lazy" />
          </Link>
          <div>
            <time dateTime={article.date}>
              {formatArticleDate(article.date)}
            </time>
            <h3>
              <Link to={`/news/${article.slug}`}>{article.title}</Link>
            </h3>
            <p>
              {article.excerpt
                .replace(/\s*\[…\].*$/, "")
                .replace(/\s*\[\.\.\.\].*$/, "")}
            </p>
            <Link to={`/news/${article.slug}`} className="kw-text-link">
              Read article <ArrowUpRight size={15} />
            </Link>
          </div>
        </article>
      ))}
    </div>
  );
}
export function LatestNews() {
  return (
    <section data-analytics-section="latest-news" className="kw-section kw-latest-news"
      aria-labelledby="latest-news-title"
    >
      <Reveal className="kw-restored-heading">
        <div>
          <span className="kw-small-label">ARTICLES & PRODUCT UPDATES</span>
          <h2 id="latest-news-title">Latest news.</h2>
          <p>Ideas, practical guidance, and updates from Karalewich.</p>
        </div>
        <Link to="/news" className="kw-text-link" data-analytics="news_all">
          View all news <ArrowUpRight size={17} />
        </Link>
      </Reveal>
      <NewsCards articles={newsArticles.slice(0, 3)} />
    </section>
  );
}
export function formatArticleDate(date) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00Z`));
}
